import { skipToken } from '@reduxjs/toolkit/query';
import { sortAsc } from '@strata/helper-functions';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { type GetIntakeCaseMilestonesProps } from 'store/api/intakeCaseApi';
import {
    useGetIntakeCasesByMemberDrugQuery,
    useGetIntakeCaseVisibiltyQuery,
    useGetMilestonesByMemberDrugQuery,
} from 'store/api/intakeCaseApi';
import {
    type ReferralDetails,
    type TreatmentDetails,
    type TreatmentReferralSummary,
} from 'store/api/treatmentApi';

dayjs.extend(utc);

type IntakeCaseMilestonesReferralFields = Pick<
    ReferralDetails,
    | 'segmentId'
    | 'memberId'
    | 'memberFirstName'
    | 'memberDOB'
    | 'productServiceOid'
    | 'receivedTime'
> & { endDate?: string };

const keyDateFromReceivedTime = (receivedTime: string | undefined): string => {
    const parsed = dayjs.utc(receivedTime?.trim());
    return parsed.isValid() ? parsed.toISOString() : '';
};

const referralReceivedUtcMs = (receivedTime: string | undefined): number | null => {
    const parsed = dayjs.utc((receivedTime ?? '').trim());
    return parsed.isValid() ? parsed.valueOf() : null;
};

/** Latest referral by received time; ties (including missing dates) use ascending referral id. */
export const getMostCurrentReferralId = (
    referralList: TreatmentReferralSummary[] | undefined,
): string | undefined => {
    if (!referralList?.length) {
        return undefined;
    }

    const referralsWithReceivedTime = referralList.map((referral) => ({
        referralId: referral.referralId,
        receivedTimeMs: referralReceivedUtcMs(referral.receivedTime) ?? Number.NEGATIVE_INFINITY,
    }));

    referralsWithReceivedTime.sort((leftReferral, rightReferral) => {
        if (leftReferral.receivedTimeMs !== rightReferral.receivedTimeMs) {
            return rightReferral.receivedTimeMs - leftReferral.receivedTimeMs;
        }

        return sortAsc(leftReferral.referralId, rightReferral.referralId);
    });

    return referralsWithReceivedTime[0]?.referralId;
};

export const getIntakeMilestoneEndDateFromReferralList = (
    referralList: TreatmentReferralSummary[] | undefined,
    activeReferralId: string | undefined,
): string | undefined => {
    if (!activeReferralId || !referralList?.length) {
        return undefined;
    }

    const endOfTodayUtc = () => dayjs.utc().endOf('day').toISOString();

    const referralsSortedByReceivedTime = referralList
        .map((referral) => ({
            referral,
            receivedTimeUtcMs: referralReceivedUtcMs(referral.receivedTime),
        }))
        .filter(
            (entry): entry is { referral: TreatmentReferralSummary; receivedTimeUtcMs: number } =>
                entry.receivedTimeUtcMs != null,
        )
        .sort((left, right) => {
            if (left.receivedTimeUtcMs !== right.receivedTimeUtcMs) {
                return left.receivedTimeUtcMs - right.receivedTimeUtcMs;
            }

            return sortAsc(left.referral.referralId, right.referral.referralId);
        });

    const activeReferralIndex = referralsSortedByReceivedTime.findIndex(
        ({ referral }) => referral.referralId === activeReferralId,
    );

    if (activeReferralIndex === -1) {
        return undefined;
    }

    const nextReferral = referralsSortedByReceivedTime[activeReferralIndex + 1]?.referral;

    if (!nextReferral) {
        return endOfTodayUtc();
    }

    const nextReceived = dayjs.utc((nextReferral.receivedTime ?? '').trim());

    if (!nextReceived.isValid()) {
        return endOfTodayUtc();
    }

    return nextReceived.toISOString();
};

export const buildIntakeCaseMilestoneReferralFieldsFromTreatment = (
    treatmentDetails: TreatmentDetails | undefined,
    referralId: string | undefined,
): IntakeCaseMilestonesReferralFields | undefined => {
    if (!treatmentDetails || !referralId) {
        return undefined;
    }

    const { referralList = [], segmentId, memberDetails, productServiceOid } = treatmentDetails;

    const matchingReferral = referralList.find((referral) => referral.referralId === referralId);

    const intakeMilestoneReceivedTime = matchingReferral?.receivedTime;

    if (
        intakeMilestoneReceivedTime == null ||
        intakeMilestoneReceivedTime === '' ||
        !segmentId ||
        !memberDetails?.memberId ||
        memberDetails.firstName == null ||
        memberDetails.dateOfBirth == null ||
        !productServiceOid
    ) {
        return undefined;
    }

    const intakeMilestoneEndDate = getIntakeMilestoneEndDateFromReferralList(
        referralList,
        referralId,
    );

    return {
        segmentId,
        productServiceOid,
        memberId: memberDetails.memberId,
        memberFirstName: memberDetails.firstName,
        memberDOB: memberDetails.dateOfBirth,
        receivedTime: intakeMilestoneReceivedTime,
        ...(intakeMilestoneEndDate != null ? { endDate: intakeMilestoneEndDate } : {}),
    };
};

const getMilestonesByMemberDrugQueryProps = (
    showIntakeTimeline: boolean | undefined,
    referralFields: IntakeCaseMilestonesReferralFields | undefined,
    visibleSegments: string[] | undefined,
): GetIntakeCaseMilestonesProps | typeof skipToken => {
    if (!showIntakeTimeline || !referralFields) {
        return skipToken;
    }

    const keyDate = keyDateFromReceivedTime(referralFields.receivedTime);
    const normalizedEndDate = referralFields.endDate;

    if (!keyDate || !visibleSegments?.includes(referralFields.segmentId)) {
        return skipToken;
    }

    if (normalizedEndDate != null && normalizedEndDate < keyDate) {
        return skipToken;
    }

    return {
        memberId: referralFields.memberId,
        memberFirstName: referralFields.memberFirstName,
        memberDOB: referralFields.memberDOB,
        segmentId: referralFields.segmentId,
        productServiceOid: referralFields.productServiceOid,
        keyDate,
        ...(normalizedEndDate != null ? { endDate: normalizedEndDate } : {}),
    };
};

export const useIntakeCasesForTreatment = ({
    treatmentDetails,
}: {
    treatmentDetails?: TreatmentDetails;
}) => {
    const { data: visibleSegments } = useGetIntakeCaseVisibiltyQuery(
        treatmentDetails?.segmentId ? undefined : skipToken,
    );

    const intakeCasesVisible =
        visibleSegments?.includes(treatmentDetails?.segmentId ?? '') ?? false;

    const intakeCasesQueryProps =
        treatmentDetails && intakeCasesVisible
            ? {
                  memberId: treatmentDetails.memberDetails?.memberId,
                  memberFirstName: treatmentDetails.memberDetails?.firstName,
                  memberDOB: treatmentDetails.memberDetails?.dateOfBirth,
                  segmentId: treatmentDetails.segmentId,
                  productServiceOid: treatmentDetails.productServiceOid,
              }
            : skipToken;

    const { data, isFetching: intakeCasesFetching } =
        useGetIntakeCasesByMemberDrugQuery(intakeCasesQueryProps);

    return {
        intakeCases: data,
        intakeCasesFetching,
        intakeCasesVisible,
    };
};

export const useIntakeCaseMilestones = ({
    referralFields,
    showIntakeTimeline,
}: {
    referralFields?: IntakeCaseMilestonesReferralFields;
    showIntakeTimeline?: boolean;
}) => {
    const { data: visibleSegments } = useGetIntakeCaseVisibiltyQuery(
        showIntakeTimeline && referralFields?.segmentId ? undefined : skipToken,
    );

    const intakeCasesVisible = visibleSegments?.includes(referralFields?.segmentId ?? '') ?? false;

    const milestonesQueryProps = getMilestonesByMemberDrugQueryProps(
        showIntakeTimeline,
        referralFields,
        visibleSegments,
    );

    const { data, isFetching } = useGetMilestonesByMemberDrugQuery(milestonesQueryProps);

    return {
        milestones: data ?? [],
        milestonesFetching: isFetching,
        intakeCasesVisible,
    };
};
